/**
 * Assinatura do serviço por condomínio (0.1.86) — quadro do Uso & Custos.
 *
 * O Dono registra até quando cada condomínio está pago; o síndico só consulta.
 * Verde = em dia · amarelo = vence nos próximos N dias · vermelho = vencida ·
 * cinza = sem data. É informativo: não bloqueia nada (para suspender, o Dono usa
 * "Bloquear condomínio"). O mesmo dado alimenta o selo do topo (AppShell).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Check, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
  api, ApiError, COR_ASSINATURA, textoAssinatura, type AssinaturasResp,
} from "../lib/api";

export function AssinaturasCard() {
  const qc = useQueryClient();
  const { data } = useQuery<AssinaturasResp>({
    queryKey: ["uso", "assinaturas"],
    queryFn: () => api.get<AssinaturasResp>("/uso/assinaturas"),
  });
  const souDono = data?.sou_dono ?? false;

  // Datas em edição por condomínio ("" = sem data).
  const [edit, setEdit] = useState<Record<number, string>>({});
  useEffect(() => {
    if (!data) return;
    const m: Record<number, string> = {};
    for (const i of data.itens) m[i.condominio_id] = i.vence_em ?? "";
    setEdit(m);
  }, [data]);
  const [salvoId, setSalvoId] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const salvar = useMutation({
    mutationFn: (v: { condominio_id: number; vence_em: string | null }) =>
      api.patch("/uso/assinaturas", v),
    onSuccess: (_r, v) => {
      setErro(null);
      setSalvoId(v.condominio_id);
      setTimeout(() => setSalvoId(null), 2000);
      qc.invalidateQueries({ queryKey: ["uso", "assinaturas"] });
    },
    onError: (e: unknown) =>
      setErro(e instanceof ApiError
        ? ((e.body as { detail?: string })?.detail ?? e.message)
        : "Não foi possível salvar."),
  });

  if (!data || data.itens.length === 0) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 mb-6">
      <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-1.5">
        <CalendarClock size={16} className="text-brand-700" />
        Assinatura do serviço por condomínio
      </h3>
      <p className="text-[11px] text-slate-500 mb-3">
        {souDono
          ? <>Registre até quando cada condomínio está pago e clique em Salvar. Fica
            <b className="text-amber-700"> amarelo</b> nos últimos {data.aviso_dias} dias e
            <b className="text-red-700"> vermelho</b> depois do vencimento — também no selo do
            topo da tela. É só um aviso: não bloqueia o condomínio.</>
          : <>Até quando a assinatura do seu condomínio está paga. Fica amarela perto do
            vencimento e vermelha se vencer — fale com a CanadaTechBR para renovar.</>}
      </p>
      <ul className="divide-y divide-slate-100">
        {data.itens.map((i) => {
          const atual = edit[i.condominio_id] ?? "";
          const mudou = atual !== (i.vence_em ?? "");
          return (
            <li key={i.condominio_id} className="flex items-center gap-3 py-2 flex-wrap">
              <span className="flex-1 min-w-[160px] text-sm text-slate-800">{i.nome}</span>
              <span className={`text-xs font-medium border rounded-md px-2 py-1 ${COR_ASSINATURA[i.nivel]}`}>
                {textoAssinatura(i)}
              </span>
              {souDono && (
                <div className="flex items-center gap-1">
                  <input
                    type="date"
                    value={atual}
                    onChange={(e) => setEdit((m) => ({ ...m, [i.condominio_id]: e.target.value }))}
                    className="px-2 py-1 border border-slate-300 rounded-md text-sm"
                    aria-label={`Vencimento da assinatura de ${i.nome}`}
                  />
                  <button
                    type="button"
                    disabled={!mudou || salvar.isPending}
                    onClick={() => salvar.mutate({ condominio_id: i.condominio_id, vence_em: atual || null })}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-brand-700 hover:bg-brand-800 disabled:opacity-40 text-white text-xs font-medium rounded-md"
                  >
                    {salvar.isPending && salvar.variables?.condominio_id === i.condominio_id
                      ? <Loader2 size={12} className="animate-spin" />
                      : salvoId === i.condominio_id ? <Check size={12} /> : null}
                    {salvoId === i.condominio_id ? "Salvo" : "Salvar"}
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {erro && <p className="text-xs text-rose-700 mt-2">{erro}</p>}
    </div>
  );
}
